import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  trackPageView,
  trackEvent,
  trackScrollDepth,
  trackTimeOnPage,
  trackButtonClick,
  trackConversion,
  trackFormSubmission,
  trackChatEvent,
  trackROICalculation,
  trackServiceInteraction,
  trackUserJourney,
  trackPerformance,
  trackError,
  trackDownload,
} from '@/lib/analytics';

/**
 * Analytics Hook
 * Provides comprehensive tracking for user interactions and page performance
 * Following React best practices and privacy guidelines
 */
export const useAnalytics = () => {
  const location = useLocation();
  const pageStartTime = useRef<number>(Date.now());
  const scrollDepth = useRef<number>(0);
  const hasTrackedScroll = useRef<Set<number>>(new Set());

  // Track page view on route change
  useEffect(() => {
    const currentTime = Date.now();
    const timeOnPreviousPage = (currentTime - pageStartTime.current) / 1000;
    
    // Track time on previous page (if not first page)
    if (pageStartTime.current > 0) {
      trackTimeOnPage(timeOnPreviousPage, location.pathname);
    }

    // Track new page view manually (since we disabled automatic page views)
    trackPageView(location.pathname, document.title);
    
    // Track user journey
    trackUserJourney('page_view', location.pathname);
    
    // Reset for new page
    pageStartTime.current = currentTime;
    scrollDepth.current = 0;
    hasTrackedScroll.current.clear();
  }, [location.pathname]);

  // Track scroll depth with throttling and RAF to avoid forced reflows
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    let ticking = false;
    
    const handleScroll = () => {
      // Use requestAnimationFrame to batch reads and avoid forced reflows
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Batch all layout reads together
          const scrollTop = window.scrollY;
          const docHeight = document.documentElement.scrollHeight - window.innerHeight;
          const scrollPercent = Math.round((scrollTop / docHeight) * 100);
          
          // Track scroll depth at 25%, 50%, 75%, 100%
          const milestones = [25, 50, 75, 100];
          milestones.forEach(milestone => {
            if (scrollPercent >= milestone && !hasTrackedScroll.current.has(milestone)) {
              trackScrollDepth(milestone);
              hasTrackedScroll.current.add(milestone);
            }
          });
          
          scrollDepth.current = scrollPercent;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Track performance metrics (only once per page load)
  useEffect(() => {
    // Only track performance on initial page load, not on route changes
    if (location.pathname !== '/') {
      return;
    }
    
    // Use a global flag to ensure performance is only tracked once per session
    if (window.hasTrackedPerformance) {
      return;
    }
    
    const trackPerformanceMetrics = () => {
      if (window.hasTrackedPerformance || !('performance' in window)) {
        return;
      }
      
      window.hasTrackedPerformance = true;
      
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        // Track page load time
        trackPerformance('page_load_time', navigation.loadEventEnd - navigation.loadEventStart);
        
        // Track DOM content loaded
        trackPerformance('dom_content_loaded', navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart);
        
        // Track first contentful paint
        const paintEntries = performance.getEntriesByType('paint');
        const fcp = paintEntries.find(entry => entry.name === 'first-contentful-paint');
        if (fcp) {
          trackPerformance('first_contentful_paint', fcp.startTime);
        }
      }
    };

    // Track performance after page loads
    if (document.readyState === 'complete') {
      trackPerformanceMetrics();
    } else {
      window.addEventListener('load', trackPerformanceMetrics);
      return () => window.removeEventListener('load', trackPerformanceMetrics);
    }
  }, [location.pathname]);

  // Track errors
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      trackError('javascript_error', `${event.message} at ${event.filename}:${event.lineno}`);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      trackError('unhandled_promise_rejection', event.reason?.toString() || 'Unknown error');
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Analytics tracking functions
  const trackButtonClickHandler = useCallback((buttonName: string, additionalData?: any) => {
    trackButtonClick(buttonName, location.pathname);
    if (additionalData) {
      trackEvent('button_click_details', 'engagement', JSON.stringify(additionalData));
    }
  }, [location.pathname]);

  const trackFormSubmissionHandler = useCallback((formName: string, formType: string, success: boolean = true) => {
    trackFormSubmission(formName, formType);
    if (success) {
      trackConversion('form_submission', 1);
    }
  }, []);

  const trackChatInteractionHandler = useCallback((eventType: string, details?: string) => {
    trackChatEvent(eventType, details);
  }, []);

  const trackROICalculationHandler = useCallback((calculationType: string, result?: any) => {
    trackROICalculation(calculationType);
    if (result) {
      trackEvent('roi_calculation_result', 'tool_usage', JSON.stringify(result));
    }
  }, []);

  const trackServiceInteractionHandler = useCallback((serviceName: string, action: string, details?: any) => {
    trackServiceInteraction(serviceName, action);
    if (details) {
      trackEvent('service_interaction_details', 'engagement', JSON.stringify(details));
    }
  }, []);

  const trackConversionHandler = useCallback((conversionType: string, value?: number, details?: any) => {
    trackConversion(conversionType, value);
    if (details) {
      trackEvent('conversion_details', 'conversion', JSON.stringify(details));
    }
  }, []);

  const trackCustomEvent = useCallback((
    action: string,
    category: string,
    label?: string,
    value?: number
  ) => {
    trackEvent(action, category, label, value);
  }, []);

  const trackEngagement = useCallback((engagementType: string, details?: any) => {
    trackEvent('user_engagement', 'engagement', engagementType);
    if (details) {
      trackEvent('engagement_details', 'engagement', JSON.stringify(details));
    }
  }, []);

  const trackDownloadHandler = useCallback((fileName: string, fileType: string, fileSize?: number) => {
    trackDownload(fileName, fileType);
    if (fileSize) {
      trackEvent('download_size', 'engagement', `${fileName}_${fileSize}bytes`);
    }
  }, []);

  const trackNavigation = useCallback((fromPage: string, toPage: string, navigationType: string) => {
    trackEvent('navigation', 'engagement', `${fromPage}_to_${toPage}_${navigationType}`);
  }, []);

  const trackSearch = useCallback((searchTerm: string, resultsCount?: number) => {
    trackEvent('search', 'engagement', searchTerm, resultsCount);
  }, []);

  const trackFilter = useCallback((filterType: string, filterValue: string) => {
    trackEvent('filter_usage', 'engagement', `${filterType}_${filterValue}`);
  }, []);

  const trackModal = useCallback((modalName: string, action: 'open' | 'close') => {
    trackEvent('modal_interaction', 'engagement', `${modalName}_${action}`);
  }, []);

  const trackVideo = useCallback((videoName: string, action: 'play' | 'pause' | 'complete') => {
    trackEvent('video_interaction', 'engagement', `${videoName}_${action}`);
  }, []);

  const trackSocialShare = useCallback((platform: string, contentType: string) => {
    trackEvent('social_share', 'engagement', `${platform}_${contentType}`);
  }, []);

  const trackNewsletterSignup = useCallback((source: string, success: boolean = true) => {
    trackEvent('newsletter_signup', 'conversion', source);
    if (success) {
      trackConversion('newsletter_signup', 1);
    }
  }, []);

  const trackResourceDownload = useCallback((resourceName: string, resourceType: string, source: string) => {
    trackDownload(resourceName, resourceType);
    trackEvent('resource_download', 'conversion', `${resourceName}_${source}`);
    trackConversion('resource_download', 1);
  }, []);

  const trackContactAttempt = useCallback((method: string, success: boolean = true) => {
    trackEvent('contact_attempt', 'conversion', method);
    if (success) {
      trackConversion('contact_attempt', 1);
    }
  }, []);

  const trackPricingInquiry = useCallback((serviceType: string, source: string) => {
    trackEvent('pricing_inquiry', 'conversion', `${serviceType}_${source}`);
    trackConversion('pricing_inquiry', 1);
  }, []);

  const trackDemoRequest = useCallback((serviceType: string, source: string) => {
    trackEvent('demo_request', 'conversion', `${serviceType}_${source}`);
    trackConversion('demo_request', 1);
  }, []);

  return {
    // Core tracking functions
    trackButtonClick: trackButtonClickHandler,
    trackFormSubmission: trackFormSubmissionHandler,
    trackChatInteraction: trackChatInteractionHandler,
    trackROICalculation: trackROICalculationHandler,
    trackServiceInteraction: trackServiceInteractionHandler,
    trackConversion: trackConversionHandler,
    trackCustomEvent,
    trackEngagement,
    trackDownload: trackDownloadHandler,
    trackNavigation,
    trackSearch,
    trackFilter,
    trackModal,
    trackVideo,
    trackSocialShare,
    trackNewsletterSignup,
    trackResourceDownload,
    trackContactAttempt,
    trackPricingInquiry,
    trackDemoRequest,
    
    // Current page info
    currentPage: location.pathname,
    scrollDepth: scrollDepth.current,
  };
};

export default useAnalytics; 